import { useState } from "react";
import type { Cue as WebVTTCue } from "webvtt-parser";
import { t } from "@/i18n-js/instance";
import { Icon } from "@circle-react-shared/Icon";
import { FullScreenModal } from "@circle-react-shared/ModalsV2/FullScreenModal";
import { Typography } from "@circle-react-uikit/Typography";
import { Cue } from "./Cue";
import { Search } from "./Search";
import { isCurrentCue } from "./helpers";
import { useShouldAutoScroll } from "./useShouldAutoScroll";

interface FullscreenViewProps {
  children: React.ReactNode;
  cues: WebVTTCue[];
  currentTime: number;
  isOpen: boolean;
  onClose: () => void;
  onSeek: (time: number) => void;
}

export const FullscreenView = ({
  isOpen,
  onClose,
  cues,
  onSeek,
  currentTime,
  children,
}: FullscreenViewProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { containerRef: cuesContainerRef, shouldAutoScroll } =
    useShouldAutoScroll();
  const onSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchTerm(e.target.value);
  const filteredCues = cues.filter(
    cue =>
      !searchTerm || cue.text.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <FullScreenModal
      shouldCloseOnOutsideClick
      shouldShowCloseButton
      isOpen={isOpen}
      contentPortalId="fullscreen-transcripts-portal"
      position="bottom"
      onClose={onClose}
    >
      <div className="flex h-full overflow-hidden">
        <div className="bg-dark relative flex-1">
          <div
            id="transcripts-fullscreenmodal-player"
            className="flex size-full items-center justify-center"
          >
            {children}
          </div>
        </div>
        <div className="border-primary flex w-1/3 max-w-sm shrink-0 flex-col border-l">
          <div className="border-primary mb-2 flex h-16 shrink-0 items-center gap-2 border-b px-4">
            <Icon type="20-ai" size={20} />
            <Typography.LabelLg weight="semibold">
              {t("media_player.transcript")}
            </Typography.LabelLg>
          </div>
          <div className="px-4 py-2">
            <Search onChange={onSearchTermChange} />
          </div>
          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-auto p-4" ref={cuesContainerRef}>
              {filteredCues.map(cue => (
                <Cue
                  key={`transcript-fullscreen-${cue.startTime}`}
                  cue={cue}
                  isCurrentCue={isCurrentCue(cue, currentTime)}
                  shouldScroll={shouldAutoScroll}
                  onSeek={onSeek}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </FullScreenModal>
  );
};
